import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { getErrorMessage } from '@/lib/api'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'
import { shiftTemplatesApi, type ShiftTemplate } from '@/services/shift-templates'

type ShiftTemplatesDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (template: ShiftTemplate) => void
}

export function ShiftTemplatesDialog({ open, onOpenChange, onCreated }: ShiftTemplatesDialogProps) {
  const location = useLocation()
  const queryClient = useQueryClient()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search) || 'local'

  const catalogQuery = useQuery({
    queryKey: ['shift-templates', serviceInstanceId],
    queryFn: () => shiftTemplatesApi.list(),
    enabled: open,
  })

  const addMutation = useMutation({
    mutationFn: (code: string) => shiftTemplatesApi.add(code),
    onSuccess: (template) => {
      toast.success(`${template.label} added`)
      void queryClient.invalidateQueries({ queryKey: ['shift-templates'] })
      onCreated?.(template)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not add this shift.'))
    },
  })

  const presets = catalogQuery.data?.presets ?? []

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="z-[160] max-w-md" data-row-action="true">
        <DialogHeader>
          <DialogTitle>Shifts</DialogTitle>
          <DialogDescription>
            Choose a shift to add it for this organisation. Nothing is saved until you select one.
          </DialogDescription>
        </DialogHeader>

        {catalogQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading shifts…</p>
        ) : catalogQuery.isError ? (
          <p className="text-sm text-destructive">{getErrorMessage(catalogQuery.error, 'Could not load shifts.')}</p>
        ) : (
          <div className="space-y-2">
            {presets.map((preset) => (
              <button
                key={preset.code}
                type="button"
                disabled={preset.saved || addMutation.isPending}
                onClick={() => {
                  if (!preset.saved) {
                    addMutation.mutate(preset.code)
                  }
                }}
                className="flex w-full items-center justify-between gap-3 rounded-lg border border-border/70 px-3 py-2 text-left transition hover:bg-muted disabled:cursor-default disabled:hover:bg-transparent"
              >
                <span>
                  <span className="block text-sm font-medium text-foreground">{preset.label}</span>
                  <span className="block text-xs text-muted-foreground">
                    {preset.start_time} – {preset.end_time}
                  </span>
                </span>
                <span className="text-xs font-medium text-muted-foreground">
                  {preset.saved ? 'Added' : 'Add'}
                </span>
              </button>
            ))}
          </div>
        )}

        <div className="flex justify-end">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
